/**
 * The ML per-prop recommendation ladder, shared by every surface that derives or renders
 * it. It lived in three server files with three different sets of cuts. Distinct from
 * `recommendation.ts`, which is the parlay engine's uppercase BET / LEAN_BET / SKIP.
 */

export const ML_RECOMMENDATIONS = ['bet', 'lean_bet', 'caution', 'skip', 'no_signal'] as const;
export type MlRecommendation = (typeof ML_RECOMMENDATIONS)[number];

/** Bet/lean/skip cuts on the 0-100 display scale. */
export interface MlDisplayThresholds {
  bet: number;
  leanBet: number;
  skip: number;
}

/**
 * Null thresholds mean the trainer declined for this head, so the model has no opinion.
 * That is `no_signal`, not `skip`: skip claims it judged the bet bad.
 */
export function deriveMlRecommendation(
  displayScore: number,
  thresholds: MlDisplayThresholds | null,
): MlRecommendation {
  if (thresholds === null) return 'no_signal';
  if (displayScore >= thresholds.bet) return 'bet';
  if (displayScore >= thresholds.leanBet) return 'lean_bet';
  if (displayScore >= thresholds.skip) return 'caution';
  return 'skip';
}

/**
 * `none` is its own tone because a head with no opinion is not a negative judgement, and
 * colouring it as one reads to the user as "the model says don't".
 */
export type MlRecommendationTone = 'positive' | 'lean' | 'neutral' | 'negative' | 'none';

export interface MlRecommendationDisplay {
  label: string;
  tone: MlRecommendationTone;
  /** Sentence for a tooltip or a title attribute on a compact chip. */
  description: string;
}

// no_signal's description must explain why a high score can sit next to it, or the
// pairing reads as a rendering bug.
const DISPLAY: Record<MlRecommendation, MlRecommendationDisplay> = {
  bet: {
    label: 'BET',
    tone: 'positive',
    description: 'Strong ML signal, model finds significant statistical edge.',
  },
  lean_bet: {
    label: 'LEAN',
    tone: 'lean',
    description: 'Moderate ML signal, model leans positive but edge is smaller.',
  },
  caution: {
    label: 'CAUTION',
    tone: 'neutral',
    description: 'Weak ML signal, proceed with caution, limited edge detected.',
  },
  skip: {
    label: 'SKIP',
    tone: 'negative',
    description: 'Model finds no edge here and advises against the bet.',
  },
  no_signal: {
    label: 'NO SIGNAL',
    tone: 'none',
    description:
      'No validated threshold for this prop type, so the model makes no call either way. The score is still shown, but a high one does not mean a bet: it has not been shown to separate winners from losers over time.',
  },
};

export function isMlRecommendation(value: unknown): value is MlRecommendation {
  return typeof value === 'string' && (ML_RECOMMENDATIONS as readonly string[]).includes(value);
}

/** Tolerates any casing and unknown values, since this reads a free-form DB column. */
export function getMlRecommendationDisplay(rec: string): MlRecommendationDisplay {
  const key = rec.toLowerCase();
  if (isMlRecommendation(key)) return DISPLAY[key];
  return { label: rec.toUpperCase(), tone: 'neutral', description: '' };
}

/**
 * Argues FOR the bet. `no_signal` does not, and does not argue against either, so callers
 * comparing it to a regression edge must check `hasOpinion` first.
 */
export function supportsBet(rec: string): boolean {
  const key = rec.toLowerCase();
  return key === 'bet' || key === 'lean_bet';
}

/** False when the model declined, so callers can suppress agreement/conflict UI. */
export function hasOpinion(rec: string): boolean {
  return rec.toLowerCase() !== 'no_signal';
}

// Worded to hold for each untrusted verdict: a league that failed the rule, one with too few legs,
// and one with no verdict for the loaded model yet.
export const LEAGUE_HELD_NO_SIGNAL_DESCRIPTION =
  "In this league the model's recent calls have not been shown to separate winners from losers, so it makes no call here. The score is still shown, but a high one does not mean a bet.";

/** BET and LEAN become NO SIGNAL where the league is not trusted; CAUTION and SKIP still show. */
export function withLeagueTrust(rec: MlRecommendation, isTrusted: boolean): MlRecommendation {
  return !isTrusted && supportsBet(rec) ? 'no_signal' : rec;
}
